/**
 * ProTierGate — reusable lock overlay for $199/mo Pro tier features.
 *
 * Renders a blurred preview of children plus a centered CTA card. Locks user
 * actions on children (pointer-events: none) so accidental clicks don't leak
 * Pro behavior. Surfaces a one-tap upgrade CTA that flows into the existing
 * Stripe checkout.
 */
import React from "react";
import { Lock } from "lucide-react";

const ProTierGate = ({
  children,
  headline = "Pro feature",
  subhead = "Upgrade to unlock the full analysis",
  features = [],
  onUpgrade,
  priceUsd = 199,
  compact = false,
}) => {
  return (
    <div
      data-testid="pro-tier-gate"
      className="relative overflow-hidden border border-zinc-200 bg-white"
    >
      {/* Blurred preview content (informative, not interactive) */}
      <div
        className="pointer-events-none select-none filter blur-[6px] opacity-60"
        aria-hidden="true"
      >
        {children}
      </div>

      {/* Centered CTA card */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          className="bg-white border-2 border-[#7C3AED] shadow-xl max-w-md w-full p-6 space-y-4"
          data-testid="pro-tier-upgrade-card"
        >
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#7C3AED] flex items-center justify-center">
              <Lock size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="font-black text-lg tracking-tight">{headline}</h3>
              <p className="text-xs text-zinc-500 leading-tight">{subhead}</p>
            </div>
          </div>

          {features.length > 0 && !compact && (
            <ul className="space-y-1.5 text-sm text-zinc-700">
              {features.slice(0, 5).map((f, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#7C3AED] font-bold">→</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={onUpgrade}
            data-testid="pro-tier-upgrade-btn"
            className="w-full bg-[#7C3AED] hover:bg-[#6929d4] text-white font-bold py-3 transition-colors"
          >
            Upgrade to Pro · ${priceUsd}/mo
          </button>
          <p className="text-xs text-center text-zinc-500">
            Cancel anytime. 7-day money-back guarantee.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProTierGate;
